import React, { startTransition, useEffect, useState } from 'react';
import { useContext } from 'react';

import { LoginContext } from 'src/lib/LoginContext';
import { Button, cn, IconCreditCard, SideMenuListItem, SideMenuListSeparator, Switch, useScrollControl } from '@riac/design-system';
import {
  IconClose,
  IconMessage,
  IconShoppingCart,
  IconUser,
  IconHand,
  IconList,
  IconTrophy,
  IconMoney,
  IconAlert,
  IconPhone,
  IconAddressBook,
} from '@riac/design-system';
import RIACLogo from '../../../../../core/assets/riac_logo_transparent_black_full.png?h=95';
import RIACGoldLogo from '../../../../../core/assets/riac_logo_transparent_gold_full.png?h=95';
import { usePrefetchSuspense } from 'src/lib/usePrefetch';
import { useFetch } from 'src/lib/FetchContext';
import { useLocation } from 'react-router-dom';
import { AccountHeaderContext } from 'src/lib/AccountHeaderContext';
import { useDebounce } from '@uidotdev/usehooks';
import { SideMenu as BASESideMenu } from '@riac/design-system';

type SideMenuProps = React.PropsWithChildren<{
  show: boolean,
  toggle: () => void,
}>;

export const SideMenu = ({show, toggle} : SideMenuProps) => {
  const { logout, isLoggedIn } = useContext(LoginContext);
  const { fetch } = useFetch();

  const getAccountInfo = async () => {
    if(!isLoggedIn) return {error: false};
    try {
      const accountReq = await fetch('/api/account/info/accountInfo', {credentials: 'include'});
      const account = await accountReq.json();
      if(account.length !== 1) {
        // this is probably a fucking string, instead of an actual error this is what we have to work with.
        // It's been 30 hours and I'm still mad about this. blame jesse
        return {error: true};
      }
      const emailReq = await fetch('/api/account/email/getAll', {credentials: 'include'});
      const emails = await emailReq.json();
      const primaryEmail = emails.find((e) => e.EmailType === 'Primary');
      return {
        account: account && account[0] || null,
        email: primaryEmail && primaryEmail.Email || null,
      };
    } catch (e) {
      console.error('Failed to get account info', e);
      return {error: true};
    }
  }

  const [accountInfo, setAccountInfo] = useState(usePrefetchSuspense(`offcanvas`, getAccountInfo).read());
  const [firstTime, setFirstTime] = useState(true);
  useEffect(() => {
    if(firstTime) startTransition(() => setFirstTime(false));
    else getAccountInfo().then((x) => startTransition(() => setAccountInfo(x)));
  }, [isLoggedIn]);
  const { bidsSubmitted, bidCart, messages } = useContext(AccountHeaderContext);

  const [darkMode, _setDarkMode] = useState(false);
  const setDarkMode = (darkMode: boolean, skipLocalStorage=false) => {
    _setDarkMode(darkMode);
    document.documentElement.setAttribute('data-mode', darkMode ? 'dark' : 'light');
    if(!skipLocalStorage) window.localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }
  useEffect(() => {
    const mm = window.matchMedia('(prefers-color-scheme: dark)');
    const theme = window.localStorage.getItem('theme');
    if(
      theme === 'dark' || (!theme
        && mm.matches)
    ) {
      setDarkMode(true, true);
    }
  }, []);
  
  const location = useLocation();

  return <BASESideMenu
    show={show}
    toggle={toggle}
    isLoggedIn={isLoggedIn}
    logout={logout}
    lightModeImage={RIACLogo}
    darkModeImage={RIACGoldLogo}
    firstName={accountInfo?.account?.FirstName}
    email={accountInfo?.email}
  >
    <SideMenuListItem label='Home' to='/' exact location={location} onPress={toggle} excludeFromTabOrder={!show}/>
    <SideMenuListItem label='Auctions' to='/gun-auctions' location={location} onPress={toggle} excludeFromTabOrder={!show}/>
    <SideMenuListItem label='Consignment' to='/consignment' location={location} onPress={toggle} excludeFromTabOrder={!show}/>
    <SideMenuListItem label='Latest' to='/news-and-events' location={location} onPress={toggle} excludeFromTabOrder={!show}/>
    <SideMenuListItem label='FAQ' to='/faq' location={location} onPress={toggle} excludeFromTabOrder={!show}/>
    <SideMenuListItem label='About us' to='/about-us' location={location} onPress={toggle} excludeFromTabOrder={!show}/>
    {isLoggedIn &&
      <SideMenuListItem label='Account' count={messages + bidCart} max={20} useNotificationBadge excludeFromTabOrder={!show}>
        <SideMenuListItem label='Profile' to='/account' exact subItem IconComponent={IconUser} location={location} onPress={toggle} excludeFromTabOrder={!show}/>
        <SideMenuListItem label='Phone Book' to='/account/phone-book' exact subItem IconComponent={IconPhone} location={location} onPress={toggle} excludeFromTabOrder={!show}/>
        <SideMenuListItem label='Address Book' to='/account/address-book' exact subItem IconComponent={IconAddressBook} location={location} onPress={toggle} excludeFromTabOrder={!show}/>
        <SideMenuListItem label='Credit Cards' to='/account/credit-cards' exact subItem IconComponent={IconCreditCard} location={location} onPress={toggle} excludeFromTabOrder={!show}/>
        <SideMenuListItem label='Bid Cart' to='/account/cart' subItem IconComponent={IconShoppingCart} count={bidCart} max={20} useNotificationBadge location={location} onPress={toggle} excludeFromTabOrder={!show}/>
        <SideMenuListItem label='My Bids' to='/account/bids' subItem IconComponent={IconHand} count={bidsSubmitted} max={999} location={location} onPress={toggle} excludeFromTabOrder={!show}/>
        <SideMenuListItem label='Messages' to='/account/messages' subItem IconComponent={IconMessage} count={messages} max={20} useNotificationBadge location={location} onPress={toggle} excludeFromTabOrder={!show}/>
        <SideMenuListItem label='Wishlist' to='/account/wishlist' subItem IconComponent={IconList} location={location} onPress={toggle} excludeFromTabOrder={!show}/>
        <SideMenuListItem label='Won Auctions' to='/account/won-auctions' subItem IconComponent={IconTrophy} location={location} onPress={toggle} excludeFromTabOrder={!show}/>
        <SideMenuListItem label='Consignment' to='/account/consignment' subItem IconComponent={IconMoney} location={location} onPress={toggle} excludeFromTabOrder={!show}/>
        <SideMenuListItem label='Text Alerts' to='/account/textalerts' subItem IconComponent={IconAlert} location={location} onPress={toggle} excludeFromTabOrder={!show}/>
      </SideMenuListItem>
    || ''}
    <SideMenuListSeparator/>
    <div className='flex justify-between px-4 items-center pb-3'>
      <Switch label='Dark mode' className='justify-between w-full py-3' labelClassName='text-text-light group-hover:text-text' onChange={() => setDarkMode(!darkMode)} checked={darkMode} excludeFromTabOrder={!show}/>
    </div>
    <div className='flex px-4 pb-3'>
      {isLoggedIn && 
        <Button variant='link' className='p-0 no-underline hover:underline' onPress={() => logout()} excludeFromTabOrder={!show}>Log out</Button>
      }
    </div>
  </BASESideMenu>
};